export type MicrophoneCaptureStatus =
  | "unsupported"
  | "idle"
  | "requesting_permission"
  | "ready"
  | "starting"
  | "recording"
  | "stopping"
  | "recorded"
  | "denied"
  | "error";

export type MicrophonePermissionState = "unknown" | "granted" | "denied";

export type MicrophoneCaptureErrorCode =
  | "insecure_context"
  | "unsupported_browser"
  | "permission_denied"
  | "no_microphone"
  | "microphone_unavailable"
  | "constraints_unsatisfied"
  | "capture_aborted"
  | "recorder_failed"
  | "invalid_state"
  | "unknown";

export interface MicrophoneCaptureSnapshot {
  status: MicrophoneCaptureStatus;
  permission: MicrophonePermissionState;
  supported: boolean;
  secureContext: boolean;
  mimeType: string | null;
  startedAt: number | null;
  durationMs: number;
  bytesCaptured: number;
  errorCode: MicrophoneCaptureErrorCode | null;
  errorMessage: string | null;
}

export interface MicrophoneRecording {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  createdAt: number;
}

export interface MicrophoneCaptureController {
  getSnapshot(): MicrophoneCaptureSnapshot;
  getRecording(): MicrophoneRecording | null;
  subscribe(listener: (snapshot: MicrophoneCaptureSnapshot) => void): () => void;
  connect(): Promise<MicrophoneCaptureSnapshot>;
  start(): Promise<MicrophoneCaptureSnapshot>;
  stop(): Promise<MicrophoneRecording>;
  discard(): MicrophoneCaptureSnapshot;
  disconnect(): MicrophoneCaptureSnapshot;
  dispose(): void;
}

interface MicrophoneCaptureDependencies {
  mediaDevices?: Pick<MediaDevices, "getUserMedia"> | null;
  MediaRecorderClass?: typeof MediaRecorder | null;
  secureContext?: boolean;
  now?: () => number;
}

const preferredMimeTypes = Object.freeze([
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
]);

const microphoneConstraints: MediaStreamConstraints = Object.freeze({
  audio: {
    channelCount: { ideal: 1 },
    echoCancellation: { ideal: true },
    noiseSuppression: { ideal: true },
    autoGainControl: { ideal: true },
  },
  video: false,
});

function chooseMimeType(MediaRecorderClass: typeof MediaRecorder): string | null {
  if (typeof MediaRecorderClass.isTypeSupported !== "function") return null;
  return preferredMimeTypes.find((type) => MediaRecorderClass.isTypeSupported(type)) ?? null;
}

function stopTracks(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}

function errorName(error: unknown): string {
  if (error instanceof DOMException) return error.name;
  if (typeof error === "object" && error !== null && "name" in error) {
    return String((error as { name?: unknown }).name ?? "");
  }
  return "";
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function classifyCaptureError(error: unknown): MicrophoneCaptureErrorCode {
  switch (errorName(error)) {
    case "NotAllowedError":
      return "permission_denied";
    case "NotFoundError":
      return "no_microphone";
    case "NotReadableError":
      return "microphone_unavailable";
    case "OverconstrainedError":
      return "constraints_unsatisfied";
    case "AbortError":
      return "capture_aborted";
    case "SecurityError":
      return "insecure_context";
    case "InvalidStateError":
      return "invalid_state";
    default:
      return "unknown";
  }
}

function createCaptureError(code: MicrophoneCaptureErrorCode, message: string): Error {
  const error = new Error(message);
  error.name = code;
  return error;
}

export function createMicrophoneCaptureController(
  dependencies: MicrophoneCaptureDependencies = {},
): MicrophoneCaptureController {
  const mediaDevices = dependencies.mediaDevices ?? navigator.mediaDevices ?? null;
  const MediaRecorderClass = dependencies.MediaRecorderClass ?? globalThis.MediaRecorder ?? null;
  const secureContext = dependencies.secureContext ?? globalThis.isSecureContext;
  const now = dependencies.now ?? Date.now;
  const listeners = new Set<(snapshot: MicrophoneCaptureSnapshot) => void>();

  const supported = Boolean(
    secureContext &&
      mediaDevices &&
      typeof mediaDevices.getUserMedia === "function" &&
      MediaRecorderClass,
  );

  let status: MicrophoneCaptureStatus = supported ? "idle" : "unsupported";
  let permission: MicrophonePermissionState = "unknown";
  let errorCode: MicrophoneCaptureErrorCode | null = supported
    ? null
    : secureContext
      ? "unsupported_browser"
      : "insecure_context";
  let captureErrorMessage: string | null = supported
    ? null
    : secureContext
      ? "이 브라우저는 안전한 마이크 녹음을 지원하지 않습니다."
      : "마이크는 HTTPS 보안 연결에서만 사용할 수 있습니다.";
  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let startedAt: number | null = null;
  let completedDurationMs = 0;
  let recording: MicrophoneRecording | null = null;
  let stopPromise: Promise<MicrophoneRecording> | null = null;
  let resolveStop: ((value: MicrophoneRecording) => void) | null = null;
  let rejectStop: ((reason: unknown) => void) | null = null;
  let disposed = false;

  function snapshot(): MicrophoneCaptureSnapshot {
    const liveDuration =
      startedAt !== null && (status === "recording" || status === "stopping")
        ? Math.max(0, now() - startedAt)
        : completedDurationMs;
    return {
      status,
      permission,
      supported,
      secureContext,
      mimeType: recorder?.mimeType || recording?.mimeType || null,
      startedAt,
      durationMs: liveDuration,
      bytesCaptured: chunks.reduce((total, chunk) => total + chunk.size, 0) || recording?.blob.size || 0,
      errorCode,
      errorMessage: captureErrorMessage,
    };
  }

  function notify(): MicrophoneCaptureSnapshot {
    const current = snapshot();
    for (const listener of listeners) listener(current);
    return current;
  }

  function assertUsable(): void {
    if (disposed) throw createCaptureError("invalid_state", "마이크 컨트롤러가 이미 종료되었습니다.");
    if (!supported) {
      throw createCaptureError(
        errorCode ?? "unsupported_browser",
        captureErrorMessage ?? "마이크 녹음을 지원하지 않습니다.",
      );
    }
  }

  function setFailure(error: unknown, fallback: MicrophoneCaptureErrorCode = "unknown"): never {
    const classified = classifyCaptureError(error);
    errorCode = classified === "unknown" ? fallback : classified;
    captureErrorMessage = errorMessage(error);
    if (errorCode === "permission_denied") {
      permission = "denied";
      status = "denied";
    } else {
      status = "error";
    }
    stopTracks(stream);
    stream = null;
    recorder = null;
    startedAt = null;
    notify();
    throw error instanceof Error
      ? error
      : createCaptureError(errorCode, captureErrorMessage);
  }

  async function requestStream(): Promise<MediaStream> {
    assertUsable();
    try {
      const nextStream = await mediaDevices!.getUserMedia(microphoneConstraints);
      if (nextStream.getAudioTracks().length === 0) {
        stopTracks(nextStream);
        throw new DOMException("사용 가능한 마이크 트랙이 없습니다.", "NotFoundError");
      }
      permission = "granted";
      return nextStream;
    } catch (error) {
      return setFailure(error);
    }
  }

  async function connect(): Promise<MicrophoneCaptureSnapshot> {
    assertUsable();
    if (status === "recording" || status === "stopping" || status === "starting") {
      throw createCaptureError("invalid_state", "녹음 중에는 마이크 연결을 다시 요청할 수 없습니다.");
    }
    status = "requesting_permission";
    errorCode = null;
    captureErrorMessage = null;
    notify();

    const permissionProbe = await requestStream();
    stopTracks(permissionProbe);
    status = recording ? "recorded" : "ready";
    startedAt = null;
    return notify();
  }

  async function start(): Promise<MicrophoneCaptureSnapshot> {
    assertUsable();
    if (recording) {
      throw createCaptureError(
        "invalid_state",
        "기존 녹음을 폐기한 뒤 새 녹음을 시작하십시오.",
      );
    }
    if (status !== "ready") {
      throw createCaptureError("invalid_state", "먼저 마이크 연결과 권한 확인을 완료하십시오.");
    }

    status = "starting";
    errorCode = null;
    captureErrorMessage = null;
    chunks = [];
    completedDurationMs = 0;
    notify();

    stream = await requestStream();
    const mimeType = chooseMimeType(MediaRecorderClass!);

    try {
      recorder = mimeType
        ? new MediaRecorderClass!(stream, { mimeType })
        : new MediaRecorderClass!(stream);
    } catch (error) {
      return setFailure(error, "recorder_failed");
    }

    recorder.addEventListener("dataavailable", (event) => {
      const blobEvent = event as BlobEvent;
      if (blobEvent.data.size > 0) {
        chunks.push(blobEvent.data);
        notify();
      }
    });
    recorder.addEventListener("error", (event) => {
      const recorderError = (event as Event & { error?: DOMException }).error ??
        createCaptureError("recorder_failed", "브라우저 녹음기에서 오류가 발생했습니다.");
      const pendingReject = rejectStop;
      try {
        setFailure(recorderError, "recorder_failed");
      } catch (error) {
        pendingReject?.(error);
      }
    });
    recorder.addEventListener("stop", () => {
      const stoppedAt = now();
      const durationMs = startedAt === null ? 0 : Math.max(0, stoppedAt - startedAt);
      const mime = recorder?.mimeType || mimeType || "application/octet-stream";
      const blob = new Blob(chunks, { type: mime });
      recording = { blob, mimeType: mime, durationMs, createdAt: stoppedAt };
      completedDurationMs = durationMs;
      stopTracks(stream);
      stream = null;
      recorder = null;
      startedAt = null;
      status = "recorded";
      errorCode = null;
      captureErrorMessage = null;
      const pendingResolve = resolveStop;
      stopPromise = null;
      resolveStop = null;
      rejectStop = null;
      notify();
      pendingResolve?.(recording);
    });

    try {
      startedAt = now();
      recorder.start(1_000);
      status = "recording";
      return notify();
    } catch (error) {
      return setFailure(error, "recorder_failed");
    }
  }

  function stop(): Promise<MicrophoneRecording> {
    assertUsable();
    if (status === "stopping" && stopPromise) return stopPromise;
    if (status !== "recording" || !recorder) {
      return Promise.reject(
        createCaptureError("invalid_state", "진행 중인 녹음이 없습니다."),
      );
    }

    status = "stopping";
    notify();
    stopPromise = new Promise<MicrophoneRecording>((resolve, reject) => {
      resolveStop = resolve;
      rejectStop = reject;
    });

    try {
      recorder.stop();
    } catch (error) {
      const pendingReject = rejectStop;
      stopPromise = null;
      resolveStop = null;
      rejectStop = null;
      try {
        setFailure(error, "recorder_failed");
      } catch (failure) {
        pendingReject?.(failure);
      }
    }
    return stopPromise ?? Promise.reject(createCaptureError("recorder_failed", "녹음을 중지하지 못했습니다."));
  }

  function discard(): MicrophoneCaptureSnapshot {
    assertUsable();
    if (status === "recording" || status === "stopping" || status === "starting") {
      throw createCaptureError("invalid_state", "녹음을 중지한 뒤 폐기하십시오.");
    }
    recording = null;
    chunks = [];
    completedDurationMs = 0;
    errorCode = null;
    captureErrorMessage = null;
    status = permission === "granted" ? "ready" : "idle";
    return notify();
  }

  function disconnect(): MicrophoneCaptureSnapshot {
    assertUsable();
    if (status === "recording" || status === "stopping" || status === "starting") {
      throw createCaptureError("invalid_state", "녹음을 중지한 뒤 마이크 연결을 해제하십시오.");
    }
    stopTracks(stream);
    stream = null;
    recorder = null;
    startedAt = null;
    status = recording ? "recorded" : "idle";
    return notify();
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    stopTracks(stream);
    stream = null;
    recorder = null;
    chunks = [];
    recording = null;
    listeners.clear();
    resolveStop = null;
    rejectStop?.(createCaptureError("capture_aborted", "마이크 녹음이 종료되었습니다."));
    rejectStop = null;
    stopPromise = null;
  }

  return {
    getSnapshot: snapshot,
    getRecording: () => recording,
    subscribe(listener) {
      listeners.add(listener);
      listener(snapshot());
      return () => listeners.delete(listener);
    },
    connect,
    start,
    stop,
    discard,
    disconnect,
    dispose,
  };
}
